import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * apps/web :: components/exam :: ExamCooldownBanner
 *
 * Shown on /exam/:stateCode when POST /api/exam/start returned 429
 * `cooldown` (or when a prior failed attempt sits within the state's
 * retryCooldownMinutes window). Displays a live mm:ss countdown until
 * the user may retry.
 *
 * Reuses the ExamCountdownTimer pattern (setInterval + Date.now() diff)
 * but kept separate here because the visual treatment is different — a
 * full-width banner rather than a compact clock, and we don't want an
 * `onTimeout` callback (the page re-polls config after the window ends).
 */
export interface ExamCooldownBannerProps {
  retryAvailableAtMs: number;
}

function formatMMSS(remainingMs: number): string {
  const seconds = Math.max(0, Math.ceil(remainingMs / 1_000));
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function ExamCooldownBanner({
  retryAvailableAtMs,
}: ExamCooldownBannerProps) {
  const { t } = useTranslation();
  const [remainingMs, setRemainingMs] = useState(() =>
    Math.max(0, retryAvailableAtMs - Date.now()),
  );

  useEffect(() => {
    const tick = () =>
      setRemainingMs(Math.max(0, retryAvailableAtMs - Date.now()));
    tick();
    const id = setInterval(tick, 1_000);
    return () => clearInterval(id);
  }, [retryAvailableAtMs]);

  return (
    <section
      role="status"
      aria-live="polite"
      data-testid="exam-cooldown-banner"
      className="mt-lg rounded-lg border border-brand-accent/40 bg-brand-accent/5 p-md"
    >
      <h2 className="font-display text-xl font-bold text-brand-primary">
        {t("exam.cooldown.heading")}
      </h2>
      <p className="mt-sm text-sm text-neutral-800">
        {t("exam.cooldown.body")}
      </p>
      <p
        data-testid="exam-cooldown-remaining"
        className="mt-md font-mono text-2xl font-semibold text-brand-primary"
      >
        {formatMMSS(remainingMs)}
      </p>
    </section>
  );
}
